import { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { clientProfileCompletionQueryKey, staleTimeInTwoHours } from '../core/reactQueryCore';
import { getProfileCompletionPercentage } from '../core/api';
import { Sections } from '../core/enums';
import { getApiErrorMessage, isApiError } from '../lib/ApiError';

let queryClient: QueryClient;

export const initializeProfileCompletionStore = (client: QueryClient) => {
	queryClient = client;
};

interface ProfileCompletionState {
	personalCompletionPercentage: number;
	businessCompletionPercentage: number;
	wealthCompletionPercentage: number;
	estateCompletionPercentage: number;
	totalCompletionPercentage: number;
	fetchProfileCompletion: () => Promise<string | null>;
	refetchProfileCompletionFromApi: () => Promise<string | null>;
}

const useProfileCompletionStore = create<ProfileCompletionState>((set) => ({
	personalCompletionPercentage: 0,
	businessCompletionPercentage: 0,
	wealthCompletionPercentage: 0,
	estateCompletionPercentage: 0,
	totalCompletionPercentage: 0,
	fetchProfileCompletion: async () => {
		try {
			const profileCompletions = await queryClient.fetchQuery({
				queryKey: clientProfileCompletionQueryKey,
				queryFn: getProfileCompletionPercentage,
				staleTime: staleTimeInTwoHours
			});
			set({ personalCompletionPercentage: profileCompletions.find((x) => x.type === Sections.Personal).percentage });
			set({ businessCompletionPercentage: profileCompletions.find((x) => x.type === Sections.Business).percentage });
			set({ wealthCompletionPercentage: profileCompletions.find((x) => x.type === Sections.Wealth).percentage });
			set({ estateCompletionPercentage: profileCompletions.find((x) => x.type === Sections.Estate).percentage });
			set({ totalCompletionPercentage: profileCompletions.find((x) => x.type === 'Total').percentage });
			return null;
		} catch (e) {
			return isApiError(e) ? getApiErrorMessage(e) : e.message;
		}
	},
	refetchProfileCompletionFromApi: async () => {
		// TODO: Consider rendering loading state while refetching
		try {
			await queryClient.invalidateQueries({ queryKey: clientProfileCompletionQueryKey });
			// Update states after refetching from API
			const error: string | null = await useProfileCompletionStore.getState().fetchProfileCompletion();
			return error;
		} catch (e) {
			return isApiError(e) ? getApiErrorMessage(e) : e.message;
		}
	}
}));

export default useProfileCompletionStore;
